import React, { useState, useEffect } from 'react';
import type { InventoryItem } from '../../types';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';

interface InventoryItemModalProps {
  item: InventoryItem | null;
  onClose: () => void;
  onSave: (item: InventoryItem) => void;
}

const units: InventoryItem['unit'][] = ['kg', 'g', 'L', 'ml', 'units'];

const InventoryItemModal: React.FC<InventoryItemModalProps> = ({ item, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    unit: 'units' as InventoryItem['unit'],
    stock: 0,
    lowStockThreshold: 0,
    isBulkPurchase: false,
    hasExpiryDate: false,
    expiryDate: '',
    category: '',
    supplier: '',
  });
  const [id, setId] = useState<number | null>(null);

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name,
        unit: item.unit,
        stock: item.stock,
        lowStockThreshold: item.lowStockThreshold || 0,
        isBulkPurchase: item.isBulkPurchase || false,
        hasExpiryDate: item.hasExpiryDate || false,
        expiryDate: item.expiryDate ? new Date(item.expiryDate).toISOString().split('T')[0] : '',
        category: item.category || '',
        supplier: item.supplier || '',
      });
      setId(item.id);
    } else {
        setFormData({
            name: '',
            unit: 'units',
            stock: 0,
            lowStockThreshold: 10,
            isBulkPurchase: false,
            hasExpiryDate: false,
            expiryDate: '',
            category: '',
            supplier: '',
        });
        setId(null);
    }
  }, [item]);

  const nameKeyboard = useSoftKeyboardField({
    id: 'inventory-item-name',
    type: 'alphanumeric',
    label: 'Item name',
    value: formData.name,
    onChange: (v) => setFormData((prev) => ({ ...prev, name: v })),
  });
  const stockKeyboard = useSoftKeyboardField({
    id: 'inventory-item-stock',
    type: 'numeric',
    label: 'Stock',
    value: formData.stock === 0 ? '' : String(formData.stock),
    onChange: (v) => setFormData((prev) => ({ ...prev, stock: v === '' ? 0 : parseFloat(v) || 0 })),
    manualValidator: (v) => v === '' || /^\d*\.?\d*$/.test(v),
  });
  const thresholdKeyboard = useSoftKeyboardField({
    id: 'inventory-item-threshold',
    type: 'numeric',
    label: 'Low stock threshold',
    value: formData.lowStockThreshold === 0 ? '' : String(formData.lowStockThreshold),
    onChange: (v) => setFormData((prev) => ({ ...prev, lowStockThreshold: v === '' ? 0 : parseFloat(v) || 0 })),
    manualValidator: (v) => v === '' || /^\d*\.?\d*$/.test(v),
  });
  const categoryKeyboard = useSoftKeyboardField({
    id: 'inventory-item-category',
    type: 'alphanumeric',
    label: 'Category',
    value: formData.category,
    onChange: (v) => setFormData((prev) => ({ ...prev, category: v })),
  });
  const supplierKeyboard = useSoftKeyboardField({
    id: 'inventory-item-supplier',
    type: 'alphanumeric',
    label: 'Supplier',
    value: formData.supplier,
    onChange: (v) => setFormData((prev) => ({ ...prev, supplier: v })),
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' 
        ? checked 
        : (name === 'lowStockThreshold' || name === 'stock') 
          ? (value === '' ? '' : (isNaN(parseFloat(value)) ? 0 : parseFloat(value)))
          : value 
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || formData.lowStockThreshold < 0 || formData.stock < 0) {
        alert('Please fill in all fields correctly. Stock and threshold must be non-negative numbers.');
        return;
    }
    if (formData.hasExpiryDate && !formData.expiryDate) {
        alert('Please enter an expiry date if the product has an expiry date.');
        return;
    }
    
    // Build the item object, omitting undefined fields (Firestore doesn't accept undefined)
    const savedItem: InventoryItem = {
      name: formData.name,
      unit: formData.unit,
      stock: formData.stock,
      lowStockThreshold: formData.lowStockThreshold,
      id: id || 0,
      lastUpdated: Date.now(),
    };
    
    // Only add optional fields if they have values
    if (formData.isBulkPurchase) {
      savedItem.isBulkPurchase = true;
    }
    
    if (formData.hasExpiryDate) {
      savedItem.hasExpiryDate = true;
    }
    
    // Only add expiryDate if it's set
    if (formData.hasExpiryDate && formData.expiryDate) {
      savedItem.expiryDate = new Date(formData.expiryDate).getTime();
    }
    
    // Only add category if it's set
    if (formData.category) {
      savedItem.category = formData.category;
    }
    
    // Only add supplier if it's set
    if (formData.supplier) {
      savedItem.supplier = formData.supplier;
    }
    
    // Preserve existing bulkPurchases if editing
    if (item && item.bulkPurchases) {
      savedItem.bulkPurchases = item.bulkPurchases;
    }
    
    onSave(savedItem);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">{item ? 'Edit Inventory Item' : 'Add New Inventory Item'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Item Name</label>
            <input type="text" name="name" id="name" value={formData.name} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500" data-soft-keyboard-handled {...nameKeyboard} required />
          </div>
          <div className="mb-4">
            <label htmlFor="unit" className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
            <select name="unit" id="unit" value={formData.unit} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500">
                {units.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div className="mb-4">
            <label htmlFor="stock" className="block text-sm font-medium text-gray-700 mb-1">Initial Stock/Quantity</label>
            <input 
              type="number" 
              name="stock" 
              id="stock" 
              value={formData.stock === 0 ? '' : formData.stock} 
              min="0" 
              step="0.01"
              data-soft-keyboard-handled
              {...stockKeyboard}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500" 
              required 
              placeholder="Enter initial quantity"
            />
            <p className="text-xs text-gray-500 mt-1">Enter the current stock quantity for this item</p>
          </div>
          <div className="mb-4">
            <label htmlFor="lowStockThreshold" className="block text-sm font-medium text-gray-700 mb-1">Low Stock Threshold</label>
            <input type="number" name="lowStockThreshold" id="lowStockThreshold" value={formData.lowStockThreshold === 0 ? '' : formData.lowStockThreshold} min="0" step="0.01" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500" data-soft-keyboard-handled {...thresholdKeyboard} required />
            <p className="text-xs text-gray-500 mt-1">Alert when stock falls below this level</p>
          </div>

          <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Additional Options</h3>
            
            <div className="mb-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="isBulkPurchase"
                  checked={formData.isBulkPurchase}
                  onChange={handleChange}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Manage via bulk purchases (e.g., buying 1000kg, allocating 30-40kg per week)</span>
              </label>
            </div>

            <div className="mb-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="hasExpiryDate"
                  checked={formData.hasExpiryDate}
                  onChange={handleChange}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Product has expiry date</span>
              </label>
            </div>

            {formData.hasExpiryDate && (
              <div className="mb-3 ml-6">
                <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                <input
                  type="date"
                  name="expiryDate"
                  id="expiryDate"
                  value={formData.expiryDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                  required={formData.hasExpiryDate}
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">Category (Optional)</label>
                <select
                  name="category"
                  id="category"
                  value={formData.category}
                  data-soft-keyboard-handled
                  {...categoryKeyboard}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                >
                  <option value="">Select category</option>
                  <option value="dry goods">Dry Goods</option>
                  <option value="perishable">Perishable</option>
                  <option value="frozen">Frozen</option>
                  <option value="beverages">Beverages</option>
                  <option value="condiments">Condiments</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="supplier" className="block text-sm font-medium text-gray-700 mb-1">Supplier (Optional)</label>
                <input
                  type="text"
                  name="supplier"
                  id="supplier"
                  value={formData.supplier}
                  data-soft-keyboard-handled
                  {...supplierKeyboard}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                  placeholder="Supplier name"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600">Save Item</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InventoryItemModal;